import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { POLYGON_COMPLETE, POLYGON_TYPE } from 'store/actions';
import { IconButton, ToggleButton, ToggleButtonGroup, Snackbar, Alert } from '@mui/material';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
// import CloseIcon from '@mui/icons-material/Close';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import PanToolIcon from '@mui/icons-material/PanTool';
// import DeleteIcon from '@mui/icons-material/Delete';
import defaultImage from '../../../../../assets/images/Camera-013_pathway(entry-samsung)_mobile-zone.jpg';
import { polygonColors } from './MiddleController';

const ImageAnnotationTool = () => {
  const [drawingMode, setDrawingMode] = useState('polygon');
  const [drawingData, setDrawingData] = useState([]);
  const [startPoint, setStartPoint] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const [scale, setScale] = useState(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const imageRef = useRef(null);

  const dispatch = useDispatch();
  const currentPolygonType = useSelector((state) => state.cameraReducer.polygonType);
  const polygonData = useSelector((state) => state.cameraReducer.polygonData);
  const cameraImage = useSelector((state) => state.cameraReducer.cameraImage);
  const zoneNames = useSelector((state) => state.cameraReducer.zoneNames);
  const zoneColors = useSelector((state) => state.cameraReducer.zoneColors);
  const borderLineNames = useSelector((state) => state.cameraReducer.borderLineNames);
  const borderLineColors = useSelector((state) => state.cameraReducer.borderLineColors);
  console.log('camera image found', cameraImage);

  const getPolygonColor = (type, index = null) => {
    if (type === 'zones') {
      const zoneName = index !== null ? zoneNames[index] : zoneNames[zoneNames.length - 1];
      return zoneColors[zoneName] || '#535BAC';
    } else if (type === 'frisking_line') {
      const borderLineName = index !== null ? borderLineNames[index] : borderLineNames[borderLineNames.length - 1];
      return borderLineColors[borderLineName] || '#0000FF';
    }
    return polygonColors[type] || '#535BAC';
  };

  const handleDrawingModeChange = (event, newMode) => {
    if (newMode !== null) {
      setDrawingMode(newMode);
    }
  };

  const handleMouseDown = (e) => {
    if (drawingMode !== 'polygon' || !currentPolygonType || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    let x = (e.clientX - rect.left) / scale;
    let y = (e.clientY - rect.top) / scale;

    // Clamp x and y to stay within the image
    x = Math.max(0, Math.min(x, rect.width / scale));
    y = Math.max(0, Math.min(y, rect.height / scale));

    if (!startPoint) {
      setStartPoint({ x, y });
    } else {
      const newPoint = { x, y };

      if (currentPolygonType === 'zones' && drawingData.length >= 2) {
        const firstPoint = drawingData[0].start;
        const distance = Math.sqrt(Math.pow(x - firstPoint.x, 2) + Math.pow(y - firstPoint.y, 2));
        if (distance < 20) {
          handleDone();
          return;
        }
      }

      setDrawingData([...drawingData, { start: startPoint, end: newPoint }]);

      if (currentPolygonType === 'frisking_line' && drawingData.length >= 1) {
        handleDone();
        return;
      }

      setStartPoint(newPoint);
    }
  };

  const handleMouseMove = (e) => {
    if (!startPoint || drawingMode !== 'polygon' || !currentPolygonType || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    let x = (e.clientX - rect.left) / scale;
    let y = (e.clientY - rect.top) / scale;

    // Clamp x and y to stay within the image
    x = Math.max(0, Math.min(x, rect.width / scale));
    y = Math.max(0, Math.min(y, rect.height / scale));

    setMousePos({ x, y });
  };

  const handleMouseUp = () => {
    // Remove automatic completion on mouse up
    // Let handleMouseDown handle the completion
  };

  const handleUndo = () => {
    if (drawingData.length > 0) {
      setDrawingData((prevData) => prevData.slice(0, -1));
      if (drawingData.length > 1) {
        setStartPoint({
          x: drawingData[drawingData.length - 2].end.x,
          y: drawingData[drawingData.length - 2].end.y
        });
      } else {
        setStartPoint(null);
      }
    }
  };

  const handleDone = () => {
    // For zones, we need 3 points (2 lines + closing line)
    if (currentPolygonType === 'zones' && drawingData.length < 2) {
      setSnackbar({
        open: true,
        message: 'A zone requires at least 3 points',
        severity: 'error'
      });
      return;
    }

    // For border lines, we need 2 points (1 line)
    if (currentPolygonType === 'frisking_line' && drawingData.length < 1) {
      setSnackbar({
        open: true,
        message: 'A border line requires at least 2 points',
        severity: 'error'
      });
      return;
    }

    let coordinates = [];

    if (currentPolygonType === 'frisking_line') {
      coordinates = [
        ...drawingData.map((line) => [line.start.x, line.start.y]).flat(),
        drawingData[drawingData.length - 1].end.x,
        drawingData[drawingData.length - 1].end.y
      ];
    } else {
      // For zones, close the polygon by connecting last point to first point
      coordinates = [
        ...drawingData.map((line) => [line.start.x, line.start.y]).flat(),
        drawingData[drawingData.length - 1].end.x,
        drawingData[drawingData.length - 1].end.y,
        drawingData[0].start.x,
        drawingData[0].start.y
      ];
    }

    dispatch({
      type: POLYGON_COMPLETE,
      payload: {
        type: currentPolygonType,
        coordinates
      }
    });

    // Reset drawing state
    setDrawingData([]);
    setStartPoint(null);
    dispatch({ type: POLYGON_TYPE, polygonType: null });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // const handleError = (error) => {
  //   const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
  //   setSnackbar({
  //     open: true,
  //     message: errorMessage,
  //     severity: 'error'
  //   });
  // };

  // const handleDeletePolygon = (type, index) => {
  //   // Implement polygon deletion logic here
  // };

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Top-left: Toggle Buttons for modes - Responsive positioning */}
      <div className="fixed top-2 left-2 sm:top-4 sm:left-4 lg:mt-20 lg:ml-[3%] z-[1000] flex gap-2 sm:gap-3">
        <ToggleButtonGroup
          value={drawingMode}
          exclusive
          onChange={handleDrawingModeChange}
          aria-label="drawing mode"
          size="small"
          sx={{
            backgroundColor: 'white',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            '& .MuiToggleButton-root': {
              padding: { xs: '6px', sm: '8px', md: '10px' },
              minWidth: { xs: '36px', sm: '40px', md: '44px' },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
        >
          <ToggleButton value="polygon" aria-label="polygon mode">
            <TimelineOutlinedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="drag" aria-label="drag mode">
            <PanToolIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {/* Main image container - Responsive */}
      <div className="w-full h-full flex items-center justify-center p-2 sm:p-4">
        <TransformWrapper
          initialScale={1}
          disabled={drawingMode === 'polygon'}
          onZoom={({ state }) => setScale(state.scale)}
          wheel={{
            step: 0.1
          }}
          pan={{
            velocityEqualToMove: true
          }}
          pinch={{ disabled: true }}
          maxScale={3}
          minScale={0.5}
        >
          <TransformComponent>
            <div
              className={`relative ${
                drawingMode === 'drag' ? 'cursor-grab active:cursor-grabbing' : 'cursor-crosshair'
              }`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            >
              <img
                ref={imageRef}
                src={cameraImage || defaultImage}
                alt="Camera Feed"
                className="max-w-full max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] object-contain rounded-lg sm:rounded-xl shadow-lg select-none"
                style={{
                  minWidth: '300px',
                  minHeight: '200px'
                }}
                onError={(e) => {
                  e.target.src = defaultImage; 
                }}
                draggable={false}
              />
              <svg
                className="absolute top-0 left-0 w-full h-full pointer-events-none"
              >
                {/* Drawing lines */}
                {drawingData.map((line, index) => (
                  <line
                    key={index}
                    x1={line.start.x}
                    y1={line.start.y}
                    x2={line.end.x}
                    y2={line.end.y}
                    stroke={getPolygonColor(currentPolygonType)}
                    strokeWidth={`${2 / scale}`}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}

                {/* Preview line */}
                {startPoint && mousePos && (
                  <line
                    x1={startPoint.x}
                    y1={startPoint.y}
                    x2={mousePos.x}
                    y2={mousePos.y}
                    stroke={getPolygonColor(currentPolygonType)}
                    strokeWidth={`${2 / scale}`}
                    strokeDasharray="5,5"
                    vectorEffect="non-scaling-stroke"
                  />
                )}

                {startPoint && (
                  <circle
                    cx={startPoint.x}
                    cy={startPoint.y}
                    r={4 / scale}
                    fill={getPolygonColor(currentPolygonType)}
                    vectorEffect="non-scaling-stroke"
                  />
                )}

                {/* Completed polygons */}
                {polygonData &&
                  Object.entries(polygonData).map(([type, typePolygons]) =>
                    Object.entries(typePolygons || {}).map(([name, points]) => {
                      if (!points || points.length === 0) return null;

                      const pointPairs = points.reduce((pairs, point, i) => {
                        if (i % 2 === 0) {
                          pairs.push(`${point},${points[i + 1]}`);
                        }
                        return pairs;
                      }, []);

                      const color = type === 'zones' ? zoneColors[name] : borderLineColors[name];

                      if (type === 'frisking_line') {
                        return (
                          <polyline
                            key={`${type}-${name}`}
                            points={pointPairs.join(' ')}
                            fill="none"
                            stroke={color}
                            strokeWidth={`${2 / scale}`}
                            vectorEffect="non-scaling-stroke"
                          />
                        );
                      }

                      return (
                        <polyline
                          key={`${type}-${name}`}
                          points={pointPairs.join(' ')}
                          fill={`${color}40`}
                          stroke={color}
                          strokeWidth={`${2 / scale}`}
                          vectorEffect="non-scaling-stroke"
                        />
                      );
                    })
                  )}
              </svg>
            </div>
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* Top-right: Undo and Done buttons - Responsive positioning */}
      <div className="fixed top-2 right-2 sm:top-4 sm:right-4 lg:mt-20 lg:mr-[29%] z-[10000] flex gap-2 bg-white rounded-lg p-1 sm:p-2 shadow-lg">
        <IconButton
          onClick={handleUndo}
          disabled={drawingMode === 'drag'}
          size="small"
          sx={{
            backgroundColor: 'white',
            color: drawingMode === 'drag' ? 'rgba(0, 0, 0, 0.26)' : 'black',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
            padding: { xs: '6px', sm: '8px' },
            '&:hover': {
              backgroundColor: drawingMode === 'drag' ? 'white' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <KeyboardReturnRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={handleDone}
          disabled={drawingMode === 'drag'}
          size="small"
          sx={{
            backgroundColor: 'white',
            color: drawingMode === 'drag' ? 'rgba(0, 0, 0, 0.26)' : 'black',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
            padding: { xs: '6px', sm: '8px' },
            '&:hover': {
              backgroundColor: drawingMode === 'drag' ? 'white' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <CheckRoundedIcon fontSize="small" />
        </IconButton>
      </div>
      
      {/* Responsive Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ 
          vertical: 'top', 
          horizontal: window.innerWidth < 640 ? 'center' : 'right' 
        }}
        sx={{
          '& .MuiSnackbarContent-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' },
            padding: { xs: '8px 16px', sm: '12px 20px' }
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ImageAnnotationTool;